import React, { useCallback, useContext } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { DatasetType } from "@mui/x-charts/internals";
import { ResponsiveChartContainer, ChartsGrid, AreaPlot, LinePlot, ChartsAxisHighlight, LineHighlightPlot, ChartsXAxis, ChartsYAxis, LineSeriesType } from "@mui/x-charts";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import CustomLegend from "../CustomLegend";
import CustomAxisTooltip from "../CustomTooltip";
import { Bucket } from "../../../../../api/indicators";
import { UserContext } from "../../../../../context/UserContext";

export interface AreaChartDataItem {
    dateTime: Date;
    [lineData: string]: number | Date;
}
interface AreaChartBaseProps {
    bucket: Bucket;
    series: (LineSeriesType)[];
    data: DatasetType;
    height: number;
    leftAxisLabel: string;
    rightAxisLabel?: string;
}

const AreaChartBase = ({bucket, series, data, height, leftAxisLabel, rightAxisLabel}: AreaChartBaseProps) => {
    const theme = useTheme();
    const { locale } = useContext(UserContext);

    const formatAxisValue = useCallback((value: Date) => {
        let format;
        switch (bucket) {
            case 'hour':
                format = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format;
                break;
            case 'day':
                format = new Intl.DateTimeFormat(locale, { month: 'short', weekday: 'short', day: '2-digit' }).format;
                break;
            case 'week':
                format = new Intl.DateTimeFormat(locale, { month: 'short', day: '2-digit' }).format;
                break;
            default:
                format = new Intl.DateTimeFormat(locale).format;
                break;
        }
        return format(value);
    }, [bucket, locale]);

    return(
        <>
            <Stack sx={{ width: '100%', marginTop: '20px' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography style={theme.typography.lineBarText}>{leftAxisLabel}</Typography>
                </Box>                
                <Box sx={{
                    width: '100%',
                    '.MuiChartsAxisHighlight-root': { strokeDasharray: 'none' },
                }}>
                    <ResponsiveChartContainer
                        series={series}
                        dataset={data}
                        height={height * 0.7}
                        xAxis={[{
                            id: 'x-axis-bottom-id',
                            dataKey: 'dateTime',
                            scaleType: 'band',
                            valueFormatter: (value: Date) => formatAxisValue(value),
                        }]}
                        yAxis={[
                            { id: 'y-axis-left-id', scaleType: 'linear' },
                            { id: 'y-axis-right-id', scaleType: 'linear', position: 'right' },
                        ]}
                        margin={{ top: 16, bottom: 32 }}
                    >
                        <ChartsGrid horizontal />
                        <AreaPlot opacity={0.2} />
                        <LinePlot />
                        <CustomAxisTooltip />
                        <ChartsAxisHighlight x="line" />
                        <LineHighlightPlot />
                        <ChartsXAxis axisId="x-axis-bottom-id" />
                        <ChartsYAxis axisId="y-axis-left-id" />
                        <ChartsYAxis axisId="y-axis-right-id" />
                    </ResponsiveChartContainer>
                </Box>
            </Stack>
            <CustomLegend series={series} />
        </>
    )
}

export default AreaChartBase;
